import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const publicApi = axios.create({ baseURL: API });

export default function InterpreterInquiryRespond() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("answer"); // "available" | "declined" | null

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [answer, setAnswer] = useState(preselect || "");
  const [confirmedRate, setConfirmedRate] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    publicApi
      .get(`/public/interpreter-inquiry/${token}`)
      .then((r) => {
        setState({ loading: false, error: null, data: r.data });
        setConfirmedRate(String(r.data.offered_rate ?? r.data.standard_rate ?? ""));
        if (r.data.response) setSubmitted(r.data); // already responded
      })
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Link invalid", data: null }));
  }, [token]);

  const submit = async () => {
    if (!answer) return toast.error("Please pick an answer");
    setSubmitting(true);
    try {
      const r = await publicApi.post(`/public/interpreter-inquiry/${token}`, {
        answer,
        confirmed_rate: confirmedRate ? parseFloat(confirmedRate) : null,
        note: note.trim() || null,
      });
      setSubmitted({ ...state.data, response: r.data.answer, confirmed_rate: parseFloat(confirmedRate || 0), response_note: note });
      toast.success("Response submitted — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center">
        <Warning size={40} className="text-amber-500 mx-auto mb-3" />
        <div className="text-lg font-bold text-slate-900 mb-1">Link not valid</div>
        <div className="text-sm text-slate-500">{state.error}</div>
      </div>
    </div>
  );

  const d = state.data;
  const dateStr = d.scheduled_at ? new Date(d.scheduled_at).toLocaleString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  if (submitted) {
    const isAvailable = submitted.response === "available";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center" data-testid="inquiry-submitted">
          {isAvailable ? <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-3" /> : <XCircle size={44} weight="fill" className="text-red-500 mx-auto mb-3" />}
          <div className="text-xl font-bold text-slate-900 mb-1">
            {isAvailable ? "Response recorded — you're marked available" : "Response recorded — you declined"}
          </div>
          <div className="text-sm text-slate-500 mb-4">
            Thank you, {d.interpreter_name}. Unity Language Group will follow up with next steps shortly.
          </div>
          {isAvailable && submitted.confirmed_rate ? (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 inline-block">
              Confirmed rate: <b>${Number(submitted.confirmed_rate).toFixed(2)}/hr</b>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="inquiry-form">
        <div className="p-5 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Unity Language Group</div>
          <div className="text-lg font-bold text-slate-900 mt-1">Availability check</div>
          <div className="text-sm text-slate-500">Hi {d.interpreter_name} — please confirm your availability below.</div>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Assignment</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">{d.project_type} — {d.language}</div>
            <div className="text-sm text-slate-700 mt-2 space-y-0.5">
              <div><span className="text-slate-400">When:</span> {dateStr}</div>
              <div><span className="text-slate-400">Duration:</span> {d.duration_hours} hour(s)</div>
              {d.location && <div><span className="text-slate-400">Location:</span> {d.location}</div>}
              <div><span className="text-slate-400">Offered rate:</span> ${(d.offered_rate ?? d.standard_rate ?? 0).toFixed(2)}/hr</div>
            </div>
            {d.notes && (
              <div className="mt-3 text-xs bg-blue-50 border-l-2 border-blue-800 p-2 text-blue-900 rounded">
                <b>Notes:</b> {d.notes}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">Your answer</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAnswer("available")}
                className={`p-3 rounded-md border-2 text-sm font-semibold transition ${answer === "available" ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-slate-200 hover:border-emerald-300"}`}
                data-testid="inquiry-answer-available"
              >
                ✓ Yes, I'm available
              </button>
              <button
                type="button"
                onClick={() => setAnswer("declined")}
                className={`p-3 rounded-md border-2 text-sm font-semibold transition ${answer === "declined" ? "border-red-600 bg-red-50 text-red-900" : "border-slate-200 hover:border-red-300"}`}
                data-testid="inquiry-answer-declined"
              >
                ✗ Can't take this one
              </button>
            </div>
          </div>

          {answer === "available" && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">Confirm your rate ($/hour)</div>
              <Input
                type="number"
                step="0.01"
                value={confirmedRate}
                onChange={(e) => setConfirmedRate(e.target.value)}
                placeholder="e.g. 65.00"
                data-testid="inquiry-confirmed-rate"
              />
              <div className="text-[11px] text-slate-500 mt-1">Adjust if this specific assignment needs a different rate — we'll use this for the confirmation.</div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Any notes for us? (optional)</div>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 'available but need to leave by 3pm' or 'not available that morning, could do afternoon'"
              data-testid="inquiry-note"
            />
          </div>

          <Button
            onClick={submit}
            disabled={submitting || !answer}
            className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11 font-semibold"
            data-testid="inquiry-submit-btn"
          >
            {submitting ? "Sending…" : "Submit response"}
          </Button>
        </div>
      </div>
    </div>
  );
}
